import React, { useEffect, useState, useRef, useCallback, memo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { fadeUp, viewport } from '../utils/animations';

const { FiPlay, FiPause, FiVolume2, FiVolumeX } = FiIcons;

const HOW_IT_WORKS_VIDEO =
  'https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/videos/dmlkZW8vbXA0/5a0eeb879e3c96c2bcabab8e2462b3eb.MP4';

const HOW_IT_WORKS_POSTER =
  'https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/images/aW1hZ2UvcG5n/51ea637dab3ab304bcf96370148198cb.png';

// ─── FAQ DATA ─────────────────────────────────────────────────────────────────
const faqs = [
  {
    title: 'What is included with my booking?',
    body: 'All sessions are completely turnkey. Your booking includes your designated set, 4K cinema cameras, professional studio lighting, broadcast-quality audio, and a dedicated operator to run the entire production. You only need to bring yourself and your guest.',
  },
  {
    title: 'What is the maximum guest capacity?',
    body: 'Capacity is strictly enforced based on your selected set to ensure pristine audio and perfect camera framing. Luxe holds up to 4 people. Nova and Echo hold up to 2 people. Vault is strictly a 1-person set.',
  },
  {
    title: 'How do I receive my video files?',
    body: 'To take your raw files home immediately for free, you must bring a high-speed portable solid-state drive (SSD). If you do not bring a hard drive, you must select the Cloud File Delivery add-on at checkout to receive a download link within 5 business days. If you purchase post-production editing, your final polished videos will be delivered to you digitally. We still highly recommend bringing an SSD to secure your original, unedited raw footage on the same day.',
  },
  {
    title: 'Do you offer post-production and video editing?',
    body: 'Yes. We offer two distinct post-production add-ons for podcast sessions: Full Podcast Episode Edit and Social Media Clips. For full episodes, our team handles the multi-camera cuts, audio cleanup, and title cards. For social clips, we pull the best moments and reframe them into vertical formats. Final polished assets are delivered within 7 to 10 days depending on the scope of the project.',
  },
  {
    title: 'Can I record multiple episodes in a single booking?',
    body: 'Yes. You can record as many segments or episodes as you like within your reserved time block. However, our editing services are strictly per-episode. If you record two episodes in one session and want both edited, you must select a quantity of 2 for your editing add-ons at checkout.',
  },
  {
    title: 'What is your cancellation and rescheduling policy?',
    body: 'We require a minimum of 24 hours notice for a full refund. If you cancel with less than 24 hours notice, the original booking fee is non-refundable, but we will apply it toward rescheduling your session for a future date.',
  },
  {
    title: 'Where are you located?',
    body: 'We are based in Fremont, CA. Ample parking is available directly in our private lot alongside convenient street parking.',
  },
  {
    title: 'When should I arrive?',
    body: 'Please arrive exactly 15 minutes before your scheduled block. This provides enough time to get comfortable, review the workflow, and get situated before we hit record.',
  },
  {
    title: 'Can I bring food or drinks into the studio?',
    body: 'Food and beverages are welcome, provided they are kept away from all technical equipment. The facility is strictly an alcohol-free and smoke-free environment.',
  },
  {
    title: 'Can I bring a remote guest into my session?',
    body: 'Yes. Select the Virtual Guest add-on at checkout. You must have your remote meeting fully set up and provide us with your active Riverside, StreamYard, or Zoom link at least 24 hours before your shoot. We route your guest directly onto an in-studio screen so you can maintain natural eye contact while we record your separate, high-quality audio and video locally.',
  },
  {
    title: 'Do you shoot on location?',
    body: 'Yes. We offer comprehensive on-location production. Our team brings our cinema cameras, lighting arrays, and audio equipment directly to your office or event space to deliver the exact same high-end standard.',
  },
  {
    title: 'Do you offer in-person studio tours?',
    body: 'Due to strict timing constraints and to protect the privacy of our active recording clients, we operate a closed set and do not offer in-person studio tours. All photos, video walkthroughs, and client examples on our website are 100% accurate. The premium standard you see is exactly what you will experience in person.',
  },
];

// ─── STRUCTURED DATA ──────────────────────────────────────────────────────────
const FAQ_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map(({ title, body }) => ({
    '@type': 'Question',
    name: title,
    acceptedAnswer: { '@type': 'Answer', text: body },
  })),
};

// ─── SEO HELPERS ──────────────────────────────────────────────────────────────
const PAGE_TITLE = 'Studio FAQs & How It Works | Bay Area Podcast Studio';
const PAGE_DESC =
  'Common questions about booking a studio session at Bay Area Podcast Studio in Fremont, CA. Covers what is included, file delivery, cancellation policy, capacity, and how the studio works.';
const PAGE_URL = 'https://bayareapodcast.studio/faq';
const OG_IMAGE = 'https://bayareapodcast.studio/og-image.jpg';

function setMeta(attr, val, content) {
  let el = document.querySelector(`meta[${attr}="${val}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, val);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function injectJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

function removeJsonLd(id) {
  const el = document.getElementById(id);
  if (el) el.remove();
}

function setCanonical(href) {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.rel = 'canonical';
    document.head.appendChild(el);
  }
  el._prev = el.href;
  el.href = href;
  return el;
}

// ─── ACCORDION ITEM ───────────────────────────────────────────────────────────
const AccordionItem = memo(function AccordionItem({ title, body, isTop }) {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className={`border-b-2 border-black${isTop ? ' border-t-2' : ''}`}>
      <button
        onClick={() => setIsOpen((o) => !o)}
        className="w-full py-5 md:py-6 text-left font-black text-base md:text-xl lg:text-2xl flex justify-between items-center bg-white hover:bg-neutral-50 transition-colors cursor-pointer focus:outline-none"
        aria-expanded={isOpen}
      >
        <span className="pr-4 leading-snug">{title}</span>
        <span
          className={`text-2xl font-light text-black transform transition-transform duration-300 shrink-0${isOpen ? ' rotate-45' : ''}`}
          aria-hidden="true"
        >
          +
        </span>
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out${
          isOpen ? ' max-h-[1000px] opacity-100' : ' max-h-0 opacity-0'
        }`}
      >
        <p className="text-sm md:text-lg lg:text-xl font-medium leading-relaxed md:leading-loose text-black pt-3 pb-6 md:pb-8">
          {body}
        </p>
      </div>
    </div>
  );
});

// ─── CUSTOM VIDEO PLAYER ──────────────────────────────────────────────────────
const HoverVideo = memo(function HoverVideo({ src, poster }) {
  const videoRef   = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted,   setIsMuted]   = useState(false);
  const [progress,  setProgress]  = useState(0);
  const [hovered,   setHovered]   = useState(false);

  useEffect(() => {
    const vid = videoRef.current;
    if (!vid) return;
    const onPlay  = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onEnded = () => { setIsPlaying(false); setProgress(0); };
    vid.addEventListener('play',  onPlay);
    vid.addEventListener('pause', onPause);
    vid.addEventListener('ended', onEnded);
    return () => {
      vid.removeEventListener('play',  onPlay);
      vid.removeEventListener('pause', onPause);
      vid.removeEventListener('ended', onEnded);
    };
  }, []);

  useEffect(() => {
    const vid = videoRef.current;
    if (!vid) return;
    const onTime = () => {
      if (vid.duration) setProgress((vid.currentTime / vid.duration) * 100);
    };
    vid.addEventListener('timeupdate', onTime);
    return () => vid.removeEventListener('timeupdate', onTime);
  }, []);

  const togglePlay = useCallback(() => {
    const vid = videoRef.current;
    if (!vid) return;
    isPlaying ? vid.pause() : vid.play();
  }, [isPlaying]);

  const toggleMute = useCallback(() => {
    const vid = videoRef.current;
    if (!vid) return;
    vid.muted = !vid.muted;
    setIsMuted(vid.muted);
  }, []);

  const handleScrub = useCallback((e) => {
    const vid = videoRef.current;
    if (!vid || !vid.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct  = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    vid.currentTime = pct * vid.duration;
    setProgress(pct * 100);
  }, []);

  const controlsVisible = isPlaying || hovered;

  return (
    <div
      className="relative mx-auto max-w-xl w-full"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Depth slab */}
      <div className="absolute inset-0 translate-x-2 translate-y-2 bg-neutral-200 -z-10" />

      <div className="relative aspect-square w-full bg-black overflow-hidden border border-white/10 shadow-2xl">
        {/* Video */}
        <video
          ref={videoRef}
          src={src}
          poster={poster}
          playsInline
          preload="none"
          className="w-full h-full object-cover"
          aria-label="How the studio works — Bay Area Podcast Studio walkthrough video"
        />

        {/* ── PERSISTENT PLAY BUTTON — always visible when paused ── */}
        {!isPlaying && (
          <div
            onClick={togglePlay}
            className="absolute inset-0 flex items-center justify-center cursor-pointer group"
            role="button"
            aria-label="Play: How the studio works"
          >
            <div className="absolute inset-0 bg-black/20 group-hover:bg-black/35 transition-colors duration-200" />
            <div className="relative z-10 flex items-center justify-center w-20 h-20 rounded-full border-2 border-white bg-black/50 backdrop-blur-sm group-hover:scale-110 group-hover:bg-black/70 transition-all duration-200">
              <SafeIcon icon={FiPlay} className="text-white text-2xl ml-1" />
            </div>
          </div>
        )}

        {/* ── MINIMAL CONTROL BAR ── */}
        <div
          className={`absolute bottom-0 left-0 right-0 transition-opacity duration-300${
            controlsVisible ? ' opacity-100' : ' opacity-0 pointer-events-none'
          }`}
        >
          <div
            className="w-full h-1 bg-white/20 cursor-pointer"
            onClick={handleScrub}
            role="slider"
            aria-label="Video progress"
            aria-valuenow={Math.round(progress)}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div
              className="h-full bg-white transition-all duration-100"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="flex items-center justify-between px-4 py-3 bg-black/60 backdrop-blur-sm">
            <button
              onClick={togglePlay}
              className="text-white hover:opacity-60 transition-opacity focus:outline-none"
              aria-label={isPlaying ? 'Pause video' : 'Play video'}
            >
              <SafeIcon icon={isPlaying ? FiPause : FiPlay} className="text-lg" />
            </button>
            <button
              onClick={toggleMute}
              className="text-white hover:opacity-60 transition-opacity focus:outline-none"
              aria-label={isMuted ? 'Unmute video' : 'Mute video'}
            >
              <SafeIcon icon={isMuted ? FiVolumeX : FiVolume2} className="text-lg" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});

// ─── PAGE ─────────────────────────────────────────────────────────────────────
export default function FAQ() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = PAGE_TITLE;

    setMeta('name',     'description',        PAGE_DESC);
    setMeta('name',     'robots',             'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1');
    setMeta('name',     'keywords',           'podcast studio FAQ, Bay Area podcast studio, how to book podcast studio, Fremont CA recording studio, 4K podcast studio questions, video podcast booking, studio policies');
    setMeta('property', 'og:type',            'website');
    setMeta('property', 'og:title',           PAGE_TITLE);
    setMeta('property', 'og:description',     PAGE_DESC);
    setMeta('property', 'og:url',             PAGE_URL);
    setMeta('property', 'og:image',           OG_IMAGE);
    setMeta('property', 'og:image:alt',       'Bay Area Podcast Studio — Studio FAQ & How It Works');
    setMeta('property', 'og:locale',          'en_US');
    setMeta('property', 'og:site_name',       'Bay Area Podcast Studio');
    setMeta('name',     'twitter:card',       'summary_large_image');
    setMeta('name',     'twitter:title',      PAGE_TITLE);
    setMeta('name',     'twitter:description',PAGE_DESC);
    setMeta('name',     'twitter:image',      OG_IMAGE);

    const canonicalEl = setCanonical(PAGE_URL);

    injectJsonLd('ld-faqpage', FAQ_JSON_LD);

    injectJsonLd('ld-breadcrumb', {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home',        item: 'https://bayareapodcast.studio/' },
        { '@type': 'ListItem', position: 2, name: 'Studio FAQs', item: PAGE_URL },
      ],
    });

    injectJsonLd('ld-video', {
      '@context': 'https://schema.org',
      '@type': 'VideoObject',
      name: 'How the Studio Works — Bay Area Podcast Studio',
      description: 'A walkthrough of how a turnkey studio session works at Bay Area Podcast Studio in Fremont, CA. Covers the booking process, set setup, studio operator role, and same-day file handoff.',
      thumbnailUrl: HOW_IT_WORKS_POSTER,
      contentUrl: HOW_IT_WORKS_VIDEO,
      uploadDate: '2024-01-01T00:00:00Z',
      publisher: {
        '@type': 'Organization',
        name: 'Bay Area Podcast Studio',
        url: 'https://bayareapodcast.studio',
      },
    });

    return () => {
      document.title = prevTitle;
      if (canonicalEl) canonicalEl.href = canonicalEl._prev || 'https://bayareapodcast.studio/';
      removeJsonLd('ld-faqpage');
      removeJsonLd('ld-breadcrumb');
      removeJsonLd('ld-video');
    };
  }, []);

  return (
    <div className="min-h-screen bg-white text-black">

      {/* ── TOP BAR ───────────────────────────────────────────────────────── */}
      <header
        className="fixed top-0 left-0 right-0 z-40 h-14 flex items-center px-6 md:px-10 border-b border-white/10 bg-black"
        role="banner"
      >
        <Link
          to="/"
          className="flex items-center gap-3 hover:opacity-60 transition-opacity"
          aria-label="Back to Bay Area Podcast Studio home"
        >
          <span className="text-white font-black text-sm tracking-[0.2em] uppercase">BA</span>
          <span className="text-white/30 text-sm select-none" aria-hidden="true">|</span>
          <span className="text-white font-black text-[11px] tracking-[0.18em] uppercase hidden sm:block">
            BAY AREA PODCAST STUDIO
          </span>
        </Link>
      </header>

      <main id="main-content" className="pt-14">

        {/* ── SECTION 1: HOW THE STUDIO WORKS ───────────────────────────── */}
        <section
          className="px-5 md:px-10 lg:px-16 pt-14 pb-16 md:pt-16 md:pb-20 border-b-2 border-black"
          aria-labelledby="how-it-works-heading"
        >
          <motion.div
            className="max-w-4xl mx-auto flex flex-col items-center text-center"
            initial="hidden"
            whileInView="visible"
            viewport={viewport}
            variants={fadeUp}
          >
            <h1
              id="how-it-works-heading"
              className="text-3xl sm:text-5xl md:text-6xl font-black text-black tracking-tight uppercase leading-none mb-10 md:mb-14"
            >
              HOW THE STUDIO WORKS
            </h1>
            <HoverVideo src={HOW_IT_WORKS_VIDEO} poster={HOW_IT_WORKS_POSTER} />
          </motion.div>
        </section>

        {/* ── SECTION 2: STUDIO FAQS & POLICIES ────────────────────────── */}
        <section
          className="px-5 md:px-10 lg:px-16 pt-16 pb-20 md:pt-20 md:pb-32 border-b-2 border-black"
          aria-labelledby="faq-heading"
        >
          <motion.div
            className="max-w-4xl mx-auto"
            initial="hidden"
            whileInView="visible"
            viewport={viewport}
            variants={fadeUp}
          >
            <h2
              id="faq-heading"
              className="text-2xl sm:text-4xl md:text-5xl font-black text-black tracking-tight uppercase mb-10 md:mb-12 text-center"
            >
              STUDIO FAQS &amp; POLICIES
            </h2>

            <div role="list" aria-label="Frequently asked questions about Bay Area Podcast Studio">
              {faqs.map((faq, i) => (
                <div key={i} role="listitem">
                  <AccordionItem title={faq.title} body={faq.body} isTop={i === 0} />
                </div>
              ))}
            </div>
          </motion.div>
        </section>

        {/* ── FOOTER ACTION BAR ─────────────────────────────────────────── */}
        <section
          className="bg-black border-t border-white/20 px-5 md:px-10 lg:px-16 py-20 md:py-28 flex flex-col items-center justify-center text-center"
          aria-label="Reserve your studio session"
        >
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={viewport}
            variants={fadeUp}
            className="flex flex-col items-center w-full"
          >
            <h2 className="text-2xl sm:text-4xl md:text-5xl font-black text-white tracking-tight uppercase mb-10">
              RESERVE YOUR SESSION
            </h2>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 w-full max-w-sm sm:max-w-none">
              <Link
                to="/studio-sessions#booking-engine"
                className="bg-white text-black text-xs md:text-sm font-bold uppercase tracking-[0.15em] px-10 py-4 w-full sm:w-auto text-center border-2 border-white hover:bg-neutral-200 transition-colors duration-150"
                aria-label="Book a studio session at Bay Area Podcast Studio"
              >
                BOOK STUDIO
              </Link>
              <Link
                to="/portfolio"
                className="bg-transparent text-white text-xs md:text-sm font-bold uppercase tracking-[0.15em] px-10 py-4 w-full sm:w-auto text-center border-2 border-white hover:bg-white hover:text-black transition-colors duration-150"
                aria-label="View our video production portfolio"
              >
                VIEW PORTFOLIO
              </Link>
            </div>
          </motion.div>
        </section>

      </main>
    </div>
  );
}